<template>
  <div class="surat-pdf-viewer">
    <template v-if="singlePdfPath || suratTugasList.length === 1">
      <div class="single-pdf-viewer">
        <div class="pdf-actions">
          <a-button class="btn-pill-red" @click="openSinglePdfPreview">
            <template #icon>
              <EyeOutlined />
            </template>
            Preview Surat
          </a-button>
          <a-button v-if="!isVerifikasiPage" class="btn-pill-black" @click="openSinglePdfNewTab">
            <template #icon>
              <ExportOutlined />
            </template>
            Buka di Tab Baru
          </a-button>
        </div>

        <!-- PDF Viewer with Controls -->
        <div class="pdf-container">
          <div v-if="loading" class="pdf-loading">
            <a-spin size="large" />
            <p>Loading PDF...</p>
          </div>
          <div v-if="error" class="pdf-error">
            <p>{{ error }}</p>
          </div>
          <div class="pdf-controls" v-if="numPages > 0 && !loading">
            <a-button @click="previousPage" :disabled="currentPage === 1">
              <template #icon><LeftOutlined /></template>
              Previous
            </a-button>
            <span class="page-info">
              Page {{ currentPage }} of {{ numPages }}
            </span>
            <a-button @click="nextPage" :disabled="currentPage === numPages">
              Next
              <template #icon><RightOutlined /></template>
            </a-button>
            <a-select v-model:value="scale" style="width: 120px" @change="onScaleChange">
              <a-select-option :value="0.5">50%</a-select-option>
              <a-select-option :value="0.75">75%</a-select-option>
              <a-select-option :value="1">100%</a-select-option>
              <a-select-option :value="1.25">125%</a-select-option>
              <a-select-option :value="1.5">150%</a-select-option>
              <a-select-option :value="2">200%</a-select-option>
            </a-select>
          </div>
          <div class="pdf-canvas-wrapper" ref="canvasWrapper">
            <canvas ref="pdfCanvas"></canvas>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="suratTugasList.length > 1">
      <div class="surat-list">
        <div class="surat-label">Surat Tugas</div>
        <div class="surat-items">
          <div v-for="(surat, index) in suratTugasList" :key="surat.id || index" class="surat-item">
            <PaperClipOutlined class="attachment-icon" />
            <a class="surat-link" @click="openPreview(surat, index)">
              {{ getSuratTitle(surat, index) }}
            </a>
            <div class="item-actions">
              <a-button type="text" class="btn-preview" @click="openPreview(surat, index)">
                <EyeOutlined />
              </a-button>
              <a-button type="text" class="btn-external" @click="openInNewTab(surat)">
                <ExportOutlined />
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="no-surat">
        <p>{{ emptyMessage }}</p>
      </div>
    </template>

    <PDFPreviewModal
      v-model:visible="previewVisible"
      :title="previewTitle"
      :pdf-path="currentPdfPath"
      :pdf-list="pdfListForModal"
      :initial-index="currentPdfIndex"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { PaperClipOutlined, EyeOutlined, ExportOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import { getFileUrl } from '@/services/axios/perki'
import PDFPreviewModal from './PDFPreviewModal.vue'

export default defineComponent({
  name: 'SuratPDFViewer',
  components: {
    PaperClipOutlined,
    EyeOutlined,
    ExportOutlined,
    LeftOutlined,
    RightOutlined,
    PDFPreviewModal,
  },
  props: {
    suratTugasList: {
      type: Array,
      default: () => [],
    },
    singlePdfPath: {
      type: String,
      default: '',
    },
    nomorSurat: {
      type: String,
      default: '',
    },
    emptyMessage: {
      type: String,
      default: 'Belum ada dokumen surat yang tersedia',
    },
  },
  setup(props) {
    const route = useRoute()
    const previewVisible = ref(false)
    const previewTitle = ref('')
    const currentPdfPath = ref('')
    const currentPdfIndex = ref(0)

    // PDF viewer state
    const pdfCanvas = ref(null)
    const canvasWrapper = ref(null)
    const currentPage = ref(1)
    const numPages = ref(0)
    const scale = ref(1)
    const loading = ref(false)
    const error = ref('')

    // PDF.js references
    let pdfDoc = null
    let pdfjsLib = null

    const isVerifikasiPage = computed(() => {
      return route.path.includes('/verifikasi')
    })

    const singlePdfUrl = computed(() => {
      if (props.singlePdfPath) {
        return getFileUrl(props.singlePdfPath)
      }
      if (props.suratTugasList.length === 1) {
        const surat = props.suratTugasList[0]
        // Use signedPdfPath if available (for hard copy), otherwise use pdfPath
        const pdfPath = surat.signedPdfPath || surat.pdfPath
        return getFileUrl(pdfPath)
      }
      return ''
    })

    const pdfListForModal = computed(() => {
      if (props.suratTugasList.length > 1) {
        return props.suratTugasList.map((surat, index) => ({
          // Use signedPdfPath if available (for hard copy), otherwise use pdfPath
          path: surat.signedPdfPath || surat.pdfPath,
          name: getSuratTitle(surat, index),
        }))
      }
      return []
    })

    const getSuratTitle = (surat, index) => {
      if (surat.perwakilan?.user?.name) {
        return `Surat Tugas ${props.nomorSurat || ''} - ${surat.perwakilan.user.name}`
      }
      if (surat.fileName) {
        return surat.fileName
      }
      return `Surat Tugas ${props.nomorSurat || ''} (${index + 1})`
    }

    // Load PDF.js dynamically from CDN
    const loadPDFJS = () => {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
          resolve(window.pdfjsLib)
          return
        }

        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
          resolve(window.pdfjsLib)
        }
        script.onerror = reject
        document.head.appendChild(script)
      })
    }

    const loadPDF = async (url) => {
      try {
        loading.value = true
        error.value = ''

        if (!pdfjsLib) {
          pdfjsLib = await loadPDFJS()
        }

        const loadingTask = pdfjsLib.getDocument(url)
        pdfDoc = await loadingTask.promise
        numPages.value = pdfDoc.numPages
        currentPage.value = 1

        await renderPage(1)
      } catch (err) {
        console.error('Error loading PDF:', err)
        error.value = 'Gagal memuat PDF. Silakan coba lagi.'
      } finally {
        loading.value = false
      }
    }

    const renderPage = async (pageNum) => {
      if (!pdfDoc || !pdfCanvas.value) return

      try {
        const page = await pdfDoc.getPage(pageNum)
        const viewport = page.getViewport({ scale: scale.value })

        const canvas = pdfCanvas.value
        const context = canvas.getContext('2d')

        // 1. Get the device pixel ratio (e.g., 2 for Retina, 1 for standard)
        const outputScale = window.devicePixelRatio || 1

        // 2. Set the internal (drawing) size to be larger based on ratio
        canvas.width = Math.floor(viewport.width * outputScale)
        canvas.height = Math.floor(viewport.height * outputScale)

        // 3. Set the visual (CSS) size to the original viewport size
        canvas.style.width = Math.floor(viewport.width) + 'px'
        canvas.style.height = Math.floor(viewport.height) + 'px'

        // 4. Create a transform matrix to scale the rendering context
        const transform = outputScale !== 1
          ? [outputScale, 0, 0, outputScale, 0, 0]
          : null

        const renderContext = {
          canvasContext: context,
          transform: transform, // Apply the scaling
          viewport: viewport,
        }

        await page.render(renderContext).promise
      } catch (err) {
        console.error('Error rendering page:', err)
        error.value = 'Gagal menampilkan halaman PDF.'
      }
    }

    const nextPage = () => {
      if (currentPage.value < numPages.value) {
        currentPage.value++
        renderPage(currentPage.value)
      }
    }

    const previousPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
        renderPage(currentPage.value)
      }
    }

    const onScaleChange = () => {
      renderPage(currentPage.value)
    }

    const openPreview = (surat, index) => {
      if (props.suratTugasList.length > 1) {
        // For multiple surats, use list navigation - don't set currentPdfPath
        // This allows the modal to use pdfList with proper index
        currentPdfPath.value = ''
        currentPdfIndex.value = index
        previewTitle.value = getSuratTitle(surat, index)
      } else {
        // For single surat, set the path directly
        currentPdfPath.value = surat.signedPdfPath || surat.pdfPath
        currentPdfIndex.value = 0
        previewTitle.value = getSuratTitle(surat, index)
      }
      previewVisible.value = true
    }

    const openInNewTab = surat => {
      // Use signedPdfPath if available (for hard copy), otherwise use pdfPath
      const pdfPath = surat.signedPdfPath || surat.pdfPath
      const url = getFileUrl(pdfPath)
      window.open(url, '_blank')
    }

    const openSinglePdfPreview = () => {
      if (props.singlePdfPath) {
        currentPdfPath.value = props.singlePdfPath
      } else if (props.suratTugasList.length === 1) {
        const surat = props.suratTugasList[0]
        // Use signedPdfPath if available (for hard copy), otherwise use pdfPath
        currentPdfPath.value = surat.signedPdfPath || surat.pdfPath
      }
      previewTitle.value = props.nomorSurat ? `Surat ${props.nomorSurat}` : 'Preview Surat'
      previewVisible.value = true
    }

    const openSinglePdfNewTab = () => {
      window.open(singlePdfUrl.value, '_blank')
    }

    // Watch for URL changes
    watch(singlePdfUrl, (newUrl) => {
      if (newUrl) {
        loadPDF(newUrl)
      }
    })

    onMounted(() => {
      if (singlePdfUrl.value) {
        loadPDF(singlePdfUrl.value)
      }
    })

    onBeforeUnmount(() => {
      if (pdfDoc) {
        pdfDoc.destroy()
      }
    })

    return {
      previewVisible,
      previewTitle,
      currentPdfPath,
      currentPdfIndex,
      pdfListForModal,
      singlePdfUrl,
      isVerifikasiPage,
      pdfCanvas,
      canvasWrapper,
      currentPage,
      numPages,
      scale,
      loading,
      error,
      getFileUrl,
      getSuratTitle,
      openPreview,
      openInNewTab,
      openSinglePdfPreview,
      openSinglePdfNewTab,
      nextPage,
      previousPage,
      onScaleChange,
    }
  },
})
</script>

<style lang="scss" scoped>
.surat-pdf-viewer {
  .single-pdf-viewer {
    .pdf-actions {
      display: flex;
      gap: 12px;
      margin-bottom: 16px;

      @media (max-width: 768px) {
        flex-direction: column;
      }
    }

    .pdf-container {
      border: 1px solid #f0f0f0;
      border-radius: 4px;
      background: #f5f5f5;
      padding: 16px;

      .pdf-loading,
      .pdf-error {
        text-align: center;
        padding: 40px;
      }

      .pdf-error {
        color: #ff4d4f;
      }

      .pdf-controls {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        justify-content: center;
        flex-wrap: wrap;

        .page-info {
          padding: 0 16px;
          font-weight: 500;
        }
      }

      .pdf-canvas-wrapper {
        overflow: auto;
        max-height: 600px;
        display: flex;

        /* 1. CRITICAL: Force alignment to start.
           This ensures that if content overflows, it spills to the RIGHT (where scrollbar works),
           not to the left (where it gets clipped). */
        justify-content: flex-start;

        background: white;
        padding: 16px;

        @media (max-width: 768px) {
          max-height: 400px;
        }

        canvas {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          display: block;

          /* 2. Use 'Safe Centering' technique.
             When container > canvas: margins grow equally -> Centered.
             When container < canvas: margins shrink to 0 -> Aligns Left (Scrollable). */
          margin: auto;

          flex-shrink: 0; /* Prevents the canvas from being squashed */
        }
      }
    }
  }

  .surat-list {
    display: flex;
    gap: 24px;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 8px;
    }

    .surat-label {
      font-weight: 500;
      color: #262626;
      min-width: 100px;
    }

    .surat-items {
      flex: 1;
    }

    .surat-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      .attachment-icon {
        color: #8c8c8c;
        flex-shrink: 0;
      }

      .surat-link {
        color: #1890ff;
        cursor: pointer;
        flex: 1;
        white-space: normal;
        word-break: break-word;

        &:hover {
          text-decoration: underline;
        }
      }

      .item-actions {
        display: flex;
        flex-shrink: 0;
      }

      .btn-preview,
      .btn-external {
        color: #e01f48;
        padding: 4px 8px;

        &:hover {
          color: #c41a3d;
          background: transparent;
        }
      }
    }
  }

  .no-surat {
    text-align: center;
    padding: 40px;
    color: #8c8c8c;
    background: #fafafa;
    border-radius: 8px;
  }
}

.btn-pill-black {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #262626;
  background: white;
  color: #262626;
  padding: 6px 16px;
  min-height: auto;
  height: 42px;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  &:hover:not(:disabled) {
    background: #262626 !important;
    border-color: #262626 !important;
    color: white !important;
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
}

.btn-pill-red {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #E10F28;
  background: #E10F28;
  color: white;
  padding: 6px 16px;
  min-height: auto;
  height: 42px;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  &:hover:not(:disabled) {
    background: white !important;
    border-color: #c41a3d !important;
    color: #c41a3d !important;
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
}
</style>
